import { useId, useState, type ReactNode } from 'react';
import { Show, Title, useRecordContext, RaRecord } from 'react-admin';
import { Alert, Card, CardContent, Collapse, Stack, Table, TableBody, Box, Typography } from '@mui/material';
import type { CardProps } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { CarEligibilities } from './CarEligibilities';
import { CarChangeHistory } from './CarChangeHistory';
import { CarOrderSuggestions } from './CarOrderSuggestions';
import { ManualAdr } from './ManualAdr';
import { VehicleSchedule } from './VehicleSchedule';
import { Row, fmtCalendarDate } from '../../shared/Panel';
import {
  AvailabilityCell,
  CompanyCell,
  PayloadCell,
  DriverCell,
  LinkedVehicleCell,
  ForkliftCell,
  formatDimensions,
} from './cells';
import { vehicleTypeLabel } from './vehicleTypes';
import { ModuleGate, companyIdOf } from '../ModuleGate';
import { PermissionGate } from '../PermissionGate';
import { ELEMENT } from '../roleCenter/elementKeys';
import { CarCardProvider } from './CarCardContext';
import { CarAlertBanners, CarSummary } from './CarSummary';
import { FeatureGate, FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  CAR_CHANGE_HISTORY,
  MANUAL_ADR,
  TRIMBLE_TERMINAL_ON_CARD,
  VEHICLE_ASSIGNMENT_HISTORY,
  VEHICLE_CARD_PROTOTYPE,
  VEHICLE_TODO_PANEL,
} from './fleetFeatures';
import { CarTodoPanel } from './CarTodoPanel';
import { CarDriverAndSet, FleetCorrectionsProvider } from './CarCorrections';
import { CarVehicleHistory } from './CarVehicleHistory';
import { CarShow as LegacyCarShow } from './legacy/CarShow';

type CarRecord = RaRecord & Record<string, unknown>;

/**
 * Zlecenia dla tego pojazdu — moduł rekomendacji spółki POJAZDU (T.01,
 * 06.10.2026); wyłączony — sekcji nie ma, API i tak odpowiada 403.
 */
const CarOrderSuggestionsSection = () => {
  const r = useRecordContext<CarRecord>();
  return (
    <ModuleGate module="vehicleSuggestions" companyId={companyIdOf(r)}>
      <CarOrderSuggestions />
    </ModuleGate>
  );
};

const formatValue = (v: unknown, unit = ''): string =>
  v === null || v === undefined || v === '' ? '—' : `${String(v)}${unit}`;

const toText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/**
 * Nagłówek karty — zwykły tytuł strony, bez ramki (omówienie 01.10.2026):
 * numer rejestracyjny pogrubiony, model i chipy w jednej linii. Do tego dnia
 * stał tu osobny boks „Pojazd”, a kartoteka nie miała rejestracji ani modelu.
 * Bez rejestracji — nazwa z kartoteki, jak na liście floty.
 */
const CarTitle = () => {
  const r = useRecordContext<CarRecord>();
  if (!r) return null;
  const model = toText(r.makeAndModel);
  return (
    <Box
      data-testid="car-title"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
        {toText(r.registrationNumber) ?? toText(r.name) ?? '—'}
      </Typography>
      {model ? (
        <Typography variant="body1" color="text.secondary">
          {model}
        </Typography>
      ) : null}
      <AvailabilityCell record={r} />
      <CompanyCell record={r} />
    </Box>
  );
};

/**
 * Spółka w kartotece pełną nazwą (na chipie w nagłówku — sam kraj). Brak
 * właściciela — to samo ostrzeżenie co na liście, nie pusty wiersz.
 */
const CompanyName = ({ record }: { record: CarRecord }) => {
  const company = record.company as { name?: unknown; country?: unknown } | null | undefined;
  if (!company) return <CompanyCell record={record} />;
  return <>{toText(company.name) ?? toText(company.country) ?? '—'}</>;
};

/**
 * Sekcja zwinięta domyślnie (prototyp p. Mariusza, zmiana 5: „Kartoteka
 * i Dane techniczne zwinięte na dole”) — w nagłówku skrót, żeby nie trzeba
 * było rozwijać po jedną wartość. Treść montowana dopiero po rozwinięciu.
 */
const CollapsibleSection = ({
  title,
  hint,
  children,
  testId,
}: {
  title: string;
  hint: string;
  children: ReactNode;
  testId: string;
}) => {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return (
    <Card variant="outlined" data-testid={testId}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((v) => !v)}
        sx={{
          all: 'unset',
          boxSizing: 'border-box',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        }}
      >
        <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {hint}
        </Typography>
        <ExpandMoreRoundedIcon
          sx={{ ml: 'auto', color: 'text.secondary', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box id={contentId} sx={{ px: 2, pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
};

/** Skrót kartoteki w nagłówku zwiniętej sekcji: spółka, typ, rocznik. */
const registryHint = (r: CarRecord) => {
  const company = r.company as { name?: unknown } | null | undefined;
  return [toText(company?.name), toText(vehicleTypeLabel(r.carType)), r.vehicleYear ? String(r.vehicleYear) : null]
    .filter(Boolean)
    .join(' · ');
};

/** Skrót danych technicznych: ładowność i wymiary. */
const technicalHint = (r: CarRecord) => {
  const t = r.maxLoadTons;
  const kg = r.permissibleLoadCapacity;
  const load =
    typeof t === 'number' && t > 0
      ? `${String(t).replace('.', ',')} t`
      : typeof kg === 'number' && kg > 0
        ? `${kg} kg`
        : null;
  const dims = formatDimensions(r);
  return [load, dims === '—' ? null : dims].filter(Boolean).join(' · ');
};

const CarRegistry = () => {
  const r = useRecordContext<CarRecord>();
  if (!r) return null;
  return (
    <CollapsibleSection title="Kartoteka" hint={registryHint(r)} testId="car-registry">
      <Table size="small">
        <TableBody>
          {/* Rejestracja, model, dostępność i spółka — w kartotece pełne
              (omówienie 01.10.2026), w nagłówku tylko skrót. Rejestracja bez
              zastępowania nazwą: pusty numer ma być widać. */}
          <Row label="Numer rejestracyjny">{formatValue(r.registrationNumber)}</Row>
          <Row label="Model">{formatValue(r.makeAndModel)}</Row>
          <Row label="Dostępność">
            <AvailabilityCell record={r} />
          </Row>
          <Row label="Spółka">
            <CompanyName record={r} />
          </Row>
          <Row label="Typ">{formatValue(vehicleTypeLabel(r.carType))}</Row>
          <Row label="Nadwozie">{formatValue(r.bodyType)}</Row>
          <Row label="Typ naczepy">{formatValue(r.trailerType)}</Row>
          <Row label="Rocznik">{formatValue(r.vehicleYear)}</Row>
          <Row label="Pierwsza rejestracja">
            {fmtCalendarDate(r.firstRegistrationDate as string)}
          </Row>
          <Row label="Kierowca">
            <DriverCell record={r} />
          </Row>
          <Row label="Drugi pojazd zestawu">
            <LinkedVehicleCell record={r} />
          </Row>
          <Row label="Opiekun FTL">{formatValue(r.ftlCaretaker)}</Row>
          <Row label="Identyfikator w TMS">{formatValue(r.tmsVehicleId)}</Row>
          {/* Nr terminala Trimble (21.10.2026, `trimbleTerminalOnCard`): to,
              co TMS opisywał jako „telefon kierowcy” (pytanie 46) — z kartoteki
              pojazdów TMS. Podpis „terminal”, nie „telefon”. */}
          <FeatureGate feature={TRIMBLE_TERMINAL_ON_CARD}>
            <Row label="Nr terminala Trimble">
              <span data-testid="car-trimble-terminal">{formatValue(r.trimbleTerminal)}</span>
            </Row>
          </FeatureGate>
        </TableBody>
      </Table>
    </CollapsibleSection>
  );
};

const CarTechnical = () => {
  const r = useRecordContext<CarRecord>();
  if (!r) return null;
  return (
    <CollapsibleSection title="Dane techniczne" hint={technicalHint(r)} testId="car-technical">
      <Table size="small">
        <TableBody>
          <Row label="Ładowność">
            <PayloadCell record={r} />
          </Row>
          <Row label="Masa całkowita">{formatValue(r.totalWeightKg, ' kg')}</Row>
          {/* Jednostka to milimetry, mimo nazw kolumn — patrz `formatDimensions`. */}
          <Row label="Wymiary (dł. × szer. × wys.)">{formatDimensions(r)}</Row>
          <Row label="Liczba osi">{formatValue(r.numberOfAxles)}</Row>
          <Row label="Wózek">
            <ForkliftCell record={r} />
          </Row>
          {/* ADR — wpis ręczny (07.10.2026): TMS i telematyka go nie podają.
              Za przełącznikiem `manualAdr` (12.10.2026). */}
          <FeatureGate feature={MANUAL_ADR}>
            <Row label="ADR">
              <ManualAdr resource="cars" />
            </Row>
          </FeatureGate>
          <Row label="Przebieg">{formatValue(r.mileageKm, ' km')}</Row>
        </TableBody>
      </Table>
    </CollapsibleSection>
  );
};

/**
 * Szczegóły pojazdu — odczyt.
 *
 * Bez przycisku edycji świadomie: właścicielem kartoteki jest TMS klienta,
 * a nasza zmiana zniknęłaby przy najbliższym imporcie. Zmiany idą u nich.
 * Wyjątki: ADR wpisywany ręcznie (07.10.2026) — tej danej TMS nie ma —
 * i korekty kartoteki (22.10.2026, `fleetCorrections`): kierowca, naczepa
 * i terminy uprawnień w osobnej tabeli, której import nie nadpisuje
 * (`CarCorrections.tsx`).
 */
/**
 * Układ karty od 07.10.2026 (prototyp p. Mariusza, zmiana 5): tytuł, pasek
 * podsumowania, baner alertu, harmonogram i rekomendacje na całą szerokość,
 * na dole uprawnienia obok zwiniętej kartoteki
 * i danych technicznych. Wcześniej kartoteka i dane techniczne stały na
 * górze rozwinięte, a harmonogram był pod nimi.
 */
const CarShowPage = () => (
  <Show title="Pojazd" component={CarShowCard}>
    <CarCardProvider>
      {/* Korekty kartoteki (22.10.2026) — kontekst dla „Kierowca i zestaw”
          i „Zmień termin”; bez przełącznika nic nie pobiera. */}
      <FleetCorrectionsProvider>
      <Stack spacing={2} sx={{ p: 2 }}>
        <CarTitle />
        <CarSummary />
        <CarAlertBanners />
        {/* Panel „Do zrobienia” (15.10.2026, makieta FTL Etap 1) — pod
            podsumowaniem, na całą szerokość (`CarTodoPanel.tsx`). */}
        <FeatureGate feature={VEHICLE_TODO_PANEL}>
          <CarTodoPanel />
        </FeatureGate>
        {/* Bez sekcji „Oferty na tym pojeździe” (omówienie 01.10.2026): czym
            auto jest zajęte, pokazuje harmonogram. API ofert
            (`/car-schedule-offers`) zostaje bez zmian. */}
        <VehicleSchedule />
        <CarOrderSuggestionsSection />
        {/* Historia pojazdu z dzienników zleceń (15.10.2026, makieta FTL Etap 1). */}
        <FeatureGate feature={VEHICLE_ASSIGNMENT_HISTORY}>
          <CarVehicleHistory />
        </FeatureGate>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            {/* „Kierowca i zestaw” z korektami (makieta FTL, FTL-08) — tylko
                z przełącznikiem `fleetCorrections`. */}
            <CarDriverAndSet />
            <CarEligibilities />
            {/* Bez sekcji „Zlecenia serwisowe z TMS” od 16.10.2026 (odpowiedź
                Marathonu, pyt. 14: zasób dla innego projektu, nie używamy). */}
            {/* Historia zmian kartoteki z TMS (12.10.2026) — zwinięta, za
                przełącznikiem `carChangeHistory`. */}
            <FeatureGate feature={CAR_CHANGE_HISTORY}>
              <CarChangeHistory />
            </FeatureGate>
          </Stack>
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <CarRegistry />
            <CarTechnical />
          </Stack>
        </Box>
      </Stack>
      </FleetCorrectionsProvider>
    </CarCardProvider>
  </Show>
);

/** Brak dostępu do floty — jedno zdanie zamiast pustej kartoteki (06.10.2026). */
export const NO_FLEET_ACCESS_TEXT = 'Nie masz dostępu do kartoteki floty.';

const NoFleetAccess = () => (
  <Alert severity="info" data-testid="no-fleet-access">
    {NO_FLEET_ACCESS_TEXT}
  </Alert>
);

/**
 * Karta pod `<Show>`. Gdy API odmówi (403), react-admin nie wylogowuje
 * (`authProvider.checkError`, `logoutUser: false`), tylko podstawia pusty
 * rekord bez `id` — sekcje pokazałyby wtedy same kreski, „brak danych”
 * i mylące „brak spółki”. Zamiast nich — komunikat o braku dostępu.
 */
const CarShowCard = ({ children, sx, ...props }: CardProps) => {
  const r = useRecordContext<CarRecord>();
  const refused = r !== undefined && (r.id === undefined || r.id === null);
  return (
    // `overflow: visible` — karta MUI przycina treść, a oś przyklejona nad
    // rekomendowanymi zleceniami (`SuggestionsAxis`, 15.10.2026) działa
    // tylko bez przycinającego przodka.
    <Card {...props} sx={[{ overflow: 'visible' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {refused ? (
        <CardContent>
          <NoFleetAccess />
        </CardContent>
      ) : (
        children
      )}
    </Card>
  );
};

/**
 * Karta pojazdu tylko z dostępem do floty (`FTL_PLANNING` — tego samego
 * wymaga API `/cars`). Gość z bezpośredniego adresu (e2e 06.10.2026) widział
 * pustą kartotekę, kręcący się harmonogram i aktywne „Pokaż rekomendowane
 * zlecenia”; teraz — jeden komunikat, bez zapytań do API.
 */
const PrototypeCarShow = () => (
  <PermissionGate
    permission="FTL_PLANNING"
    element={ELEMENT.fleetCarCard}
    fallback={
      <Card sx={{ mt: 1 }}>
        <Title title="Pojazd" />
        <CardContent>
          <NoFleetAccess />
        </CardContent>
      </Card>
    }
  >
    <CarShowPage />
  </PermissionGate>
);

/**
 * Karta pojazdu według przełącznika `vehicleCardPrototype` (decyzja
 * użytkownika 12.10.2026): włączony — układ wg prototypu p. Mariusza
 * (0.31.5), wyłączony — karta z produkcji 0.31.3 bez zmian
 * (`legacy/CarShow.tsx`, kopia z `main`). Zanim przełączniki przyjdą —
 * ładowanie, nie karta obecna (wcześniej w wersji „nowej” na chwilę
 * mignęła karta z produkcji; integracja przełączników 12.10.2026). Po
 * błędzie odczytu — karta obecna.
 */
export const CarShow = () => {
  const prototype = useFeatureState(VEHICLE_CARD_PROTOTYPE);
  if (prototype === undefined) return <FeaturesLoading />;
  return prototype ? <PrototypeCarShow /> : <LegacyCarShow />;
};
